import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Calendar, CheckCircle2, Clock, ShieldCheck, FileText, Phone, MessageCircle, Image as ImageIcon, AlertCircle, Loader2, KeyRound, Check, X } from "lucide-react";
import SEO from "@/components/SEO";
import { api, mediaUrl } from "@/lib/api";
import { useSite, whatsappLink } from "@/context/SiteContext";
import { toast } from "sonner";

const Card = ({ title, icon: Icon, children }) => (
  <div className="ascendia-card p-6">
    {title && <div className="flex items-center gap-2 mb-4">{Icon && <Icon className="h-5 w-5 text-gold-soft" />}<h3 className="font-display text-lg font-semibold text-white">{title}</h3></div>}
    {children}
  </div>
);

const STATUS_STYLE = {
  Scheduled: "text-blue-300 border-blue-500/30 bg-blue-500/10",
  Confirmed: "text-emerald-300 border-emerald-500/30 bg-emerald-500/10",
  "In Progress": "text-amber-300 border-amber-500/30 bg-amber-500/10",
  "Final Inspection": "text-purple-300 border-purple-500/30 bg-purple-500/10",
  Completed: "text-green-300 border-green-600/30 bg-green-600/10",
  Cancelled: "text-red-300 border-red-500/30 bg-red-500/10",
};

function CodeEntry() {
  const nav = useNavigate();
  const [code, setCode] = useState("");
  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4">
      <div className="w-full max-w-sm text-center">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-white/5 border border-[color:var(--border-subtle)]"><KeyRound className="h-6 w-6 text-gold-soft" /></div>
        <h1 className="font-display text-2xl font-semibold text-white">Client Portal</h1>
        <p className="text-sm text-muted-c mt-1 mb-6">Enter the access code from your Ascendia confirmation link.</p>
        <input value={code} onChange={(e) => setCode(e.target.value.trim())} data-testid="portal-code-input" placeholder="Access code" className="w-full rounded-xl bg-black/40 border border-[color:var(--border-subtle)] px-4 py-3 text-sm text-white placeholder:text-white/35 focus:outline-none focus:ring-2 focus:ring-[color:var(--ring)]" />
        <button onClick={() => code && nav(`/portal/${code}`)} data-testid="portal-code-submit" className="mt-4 w-full rounded-xl bg-gold px-6 py-3 text-sm font-semibold text-black hover:bg-gold-soft">Open my portal</button>
      </div>
    </div>
  );
}

export default function ClientPortal() {
  const { token } = useParams();
  const site = useSite();
  const [job, setJob] = useState(null);
  const [state, setState] = useState("loading");
  const [approveName, setApproveName] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (!token) return;
    api.get(`/portal/${token}`).then(({ data }) => { setJob(data); setState("ok"); })
      .catch(() => setState("notfound"));
  }, [token]);
  useEffect(() => { setState("loading"); load(); }, [load]);

  if (!token) return <><SEO title="Client Portal" noindex /><CodeEntry /></>;
  if (state === "loading") return <div className="container-ascendia py-32 text-center text-muted-c">Loading your portal…</div>;
  if (state === "notfound") return (
    <div className="container-ascendia py-32 text-center">
      <AlertCircle className="h-10 w-10 text-gold-soft mx-auto" />
      <h1 className="mt-4 font-display text-2xl font-semibold text-white">Portal link not found</h1>
      <p className="mt-2 text-muted-c">This link may be invalid or expired. Please check your confirmation message or contact us.</p>
    </div>
  );

  const approveScope = async () => {
    if (!approveName.trim()) { toast.error("Please type your full name to approve."); return; }
    setBusy(true);
    try { const { data } = await api.post(`/portal/${token}/approve-scope`, { name: approveName, origin: window.location.origin }); setJob({ ...job, scope_approval: data.scope_approval }); toast.success("Scope approved — thank you!"); }
    catch { toast.error("Could not approve. Please try again."); } finally { setBusy(false); }
  };
  const respondCO = async (coId, decision) => {
    setBusy(true);
    try { await api.post(`/portal/${token}/change-orders/${coId}/respond`, { decision }); load(); toast.success(decision === "approve" ? "Additional work approved" : "Additional work declined"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not submit response"); } finally { setBusy(false); }
  };

  const money = (n) => (n || n === 0) ? `${site.currency || "₪"}${Number(n).toLocaleString()}` : "—";
  const rep = job.report || {};
  const pendingCOs = (job.change_orders || []).filter((c) => c.status === "Pending");
  const otherCOs = (job.change_orders || []).filter((c) => c.status !== "Pending");
  const approvedCOs = (job.change_orders || []).filter((c) => c.status === "Approved");

  return (
    <>
      <SEO title="Client Portal" noindex />
      <div className="container-ascendia py-12">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <p className="kicker">Ascendia Client Portal</p>
            <h1 className="mt-1 font-display text-3xl font-semibold text-white">Hello, {job.client_name?.split(" ")[0]}</h1>
            <p className="text-xs font-mono text-gold-soft mt-1">{job.ref}</p>
          </div>
          <span className={`text-sm px-3 py-1.5 rounded-full border ${STATUS_STYLE[job.status] || "border-white/20 text-white/70"}`} data-testid="portal-status-badge">{job.status}</span>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {/* Appointment */}
            <Card title="Your appointment" icon={Calendar}>
              <div className="grid sm:grid-cols-2 gap-4 text-sm">
                <div className="flex items-center gap-2 text-secondary"><Calendar className="h-4 w-4 text-gold-soft" /> {job.appointment_date || "To be confirmed"}</div>
                <div className="flex items-center gap-2 text-secondary"><Clock className="h-4 w-4 text-gold-soft" /> {job.appointment_time || "To be confirmed"}</div>
                <div className="text-secondary">Service: <span className="text-white">{job.service || "—"}</span></div>
                <div className="text-secondary">Location: <span className="text-white">{[job.city, job.address].filter(Boolean).join(", ") || "—"}</span></div>
              </div>
            </Card>

            {/* Scope + digital approval */}
            <Card title="Agreed scope of work" icon={ShieldCheck}>
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <p className="text-xs uppercase tracking-[0.14em] text-gold mb-2">Included</p>
                  <ul className="space-y-1.5">{(job.scope_included || []).map((s) => <li key={s} className="flex items-start gap-2 text-sm text-secondary"><Check className="h-4 w-4 text-gold mt-0.5" /> {s}</li>)}{!job.scope_included?.length && <li className="text-sm text-muted-c">To be confirmed.</li>}</ul>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.14em] text-muted-c mb-2">Not included / exclusions</p>
                  <ul className="space-y-1.5">{(job.scope_excluded || []).map((s) => <li key={s} className="flex items-start gap-2 text-sm text-secondary"><X className="h-4 w-4 text-muted-c mt-0.5" /> {s}</li>)}{!job.scope_excluded?.length && <li className="text-sm text-muted-c">None specified.</li>}</ul>
                </div>
              </div>
              <div className="gold-hairline my-5" />
              {job.scope_approval?.approved ? (
                <div className="flex items-center gap-2 text-emerald-300 text-sm" data-testid="portal-scope-approved">
                  <CheckCircle2 className="h-5 w-5" /> Scope approved by {job.scope_approval.name} on {(job.scope_approval.at || "").slice(0, 10)}
                </div>
              ) : (
                <div className="rounded-xl border border-[color:var(--border-strong)] bg-white/[0.02] p-4">
                  <p className="text-sm text-secondary mb-3">Please review the scope above and digitally approve it before we begin. This confirms what's included and excluded.</p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <input value={approveName} onChange={(e) => setApproveName(e.target.value)} data-testid="portal-approve-name-input" placeholder="Type your full name" className="flex-1 rounded-xl bg-black/40 border border-[color:var(--border-subtle)] px-4 py-2.5 text-sm text-white placeholder:text-white/35 focus:outline-none focus:ring-2 focus:ring-[color:var(--ring)]" />
                    <button onClick={approveScope} disabled={busy} data-testid="portal-approve-scope-button" className="inline-flex items-center justify-center gap-2 rounded-xl bg-gold px-5 py-2.5 text-sm font-semibold text-black hover:bg-gold-soft disabled:opacity-50">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Approve scope"}</button>
                  </div>
                </div>
              )}
            </Card>

            {/* Change orders */}
            {(pendingCOs.length > 0 || otherCOs.length > 0) && (
              <Card title="Additional work requests" icon={AlertCircle}>
                {pendingCOs.map((co) => (
                  <div key={co.id} className="rounded-xl border border-[color:var(--border-strong)] bg-white/[0.02] p-4 mb-3" data-testid="portal-change-order">
                    <div className="flex justify-between items-start gap-3">
                      <p className="text-sm text-secondary">{co.description}</p>
                      <span className="text-gold-soft font-semibold whitespace-nowrap">+{money(co.price)}</span>
                    </div>
                    {co.media?.length > 0 && (
                      <div className="mt-3 flex gap-2 flex-wrap" data-testid="portal-co-photos">
                        {co.media.map((m, i) => <a key={m.url || `co-${i}`} href={mediaUrl(m.url)} target="_blank" rel="noopener noreferrer"><img src={mediaUrl(m.url)} alt="Additional work" className="h-16 w-16 rounded-lg object-cover border border-[color:var(--border-subtle)]" /></a>)}
                      </div>
                    )}
                    <div className="mt-3 flex gap-3">
                      <button onClick={() => respondCO(co.id, "approve")} disabled={busy} data-testid="portal-co-approve" className="inline-flex items-center gap-1 rounded-lg bg-gold px-4 py-2 text-xs font-semibold text-black hover:bg-gold-soft disabled:opacity-50"><Check className="h-4 w-4" /> Approve</button>
                      <button onClick={() => respondCO(co.id, "decline")} disabled={busy} data-testid="portal-co-decline" className="inline-flex items-center gap-1 rounded-lg border border-[color:var(--border-subtle)] px-4 py-2 text-xs font-semibold text-white hover:bg-white/5 disabled:opacity-50"><X className="h-4 w-4" /> Decline</button>
                    </div>
                  </div>
                ))}
                {otherCOs.map((co) => (
                  <div key={co.id} className="flex justify-between items-center py-2 border-b border-[color:var(--border-subtle)]/40 text-sm">
                    <span className="text-secondary">{co.description} <span className="text-gold-soft">+{money(co.price)}</span></span>
                    <span className={co.status === "Approved" ? "text-emerald-300" : "text-red-300"}>{co.status}</span>
                  </div>
                ))}
              </Card>
            )}

            {/* After-service report */}
            {rep.generated && (
              <Card title="After-service report" icon={FileText}>
                {job.final_inspection?.passed && (
                  <div className="mb-4 flex items-center gap-2 rounded-xl border border-gold/40 bg-gold/10 px-4 py-3" data-testid="portal-inspection-passed">
                    <ShieldCheck className="h-5 w-5 text-gold" />
                    <span className="text-sm font-semibold text-gold">Ascendia Final Inspection Passed</span>
                  </div>
                )}
                <p className="text-sm text-secondary">Completed {rep.completion_date || (job.updated_at || "").slice(0, 10)}. Service: {job.service}.</p>
                {rep.notable_areas && <p className="mt-3 text-sm"><span className="text-muted-c">Notable areas detailed:</span> <span className="text-secondary">{rep.notable_areas}</span></p>}
                {rep.issues_found && <p className="mt-2 text-sm"><span className="text-muted-c">Issues found:</span> <span className="text-secondary">{rep.issues_found}</span></p>}
                {rep.recommendations && <p className="mt-2 text-sm"><span className="text-muted-c">Recommendations:</span> <span className="text-secondary">{rep.recommendations}</span></p>}
                {approvedCOs.length > 0 && (
                  <div className="mt-3 text-sm">
                    <span className="text-muted-c">Additional work approved:</span>
                    <ul className="mt-1 space-y-1">{approvedCOs.map((c) => <li key={c.id} className="text-secondary flex justify-between"><span>{c.description}</span><span className="text-gold-soft">+{money(c.price)}</span></li>)}</ul>
                  </div>
                )}
                <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm">
                  <span className="text-muted-c">Payment: </span>
                  <span className={job.deposit_paid ? "text-emerald-300" : "text-secondary"}>Deposit {job.deposit_paid ? "paid" : "due"}</span>
                  <span className={job.balance_paid ? "text-emerald-300" : "text-secondary"}>Balance {job.balance_paid ? "paid" : "due"}</span>
                </div>
                {(rep.before_media?.length > 0 || rep.after_media?.length > 0) && (
                  <div className="mt-4 grid grid-cols-2 gap-4">
                    <div><p className="text-xs text-muted-c mb-2">Before</p><div className="grid grid-cols-2 gap-2">{(rep.before_media || []).map((m, i) => <img key={m.url || `b-${i}`} src={mediaUrl(m.url)} alt="before" className="rounded-lg border border-[color:var(--border-subtle)] object-cover aspect-square" />)}</div></div>
                    <div><p className="text-xs text-muted-c mb-2">After</p><div className="grid grid-cols-2 gap-2">{(rep.after_media || []).map((m, i) => <img key={m.url || `a-${i}`} src={mediaUrl(m.url)} alt="after" className="rounded-lg border border-[color:var(--border-subtle)] object-cover aspect-square" />)}</div></div>
                  </div>
                )}
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <Card title="Quote & payments">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-muted-c">Quote</span><span className="text-white">{money(job.quote_amount)}</span></div>
                <div className="flex justify-between"><span className="text-muted-c">Deposit</span><span className={job.deposit_paid ? "text-emerald-300" : "text-white"}>{money(job.deposit_amount)} {job.deposit_paid ? "· Paid" : "· Due"}</span></div>
                <div className="flex justify-between"><span className="text-muted-c">Balance</span><span className={job.balance_paid ? "text-emerald-300" : "text-white"}>{money(job.balance_amount)} {job.balance_paid ? "· Paid" : "· Due"}</span></div>
              </div>
            </Card>
            <Card title="Before your appointment">
              <ul className="space-y-2">{(job.prep_instructions || []).map((p, i) => <li key={`${p}-${i}`} className="flex items-start gap-2 text-sm text-secondary"><Check className="h-4 w-4 text-gold-soft mt-0.5 flex-shrink-0" /> {p}</li>)}</ul>
            </Card>
            {job.media?.length > 0 && (
              <Card title="Your uploaded media"><div className="grid grid-cols-3 gap-2">{job.media.map((m, i) => (
                <a key={m.url || `m-${i}`} href={mediaUrl(m.url)} target="_blank" rel="noopener noreferrer" className="aspect-square rounded-lg overflow-hidden border border-[color:var(--border-subtle)] bg-black/40 flex items-center justify-center">
                  {m.content_type?.startsWith("image") ? <img src={mediaUrl(m.url)} alt="" className="h-full w-full object-cover" /> : <ImageIcon className="h-5 w-5 text-gold-soft" />}
                </a>))}</div>
              </Card>
            )}
            <Card title="Contact Ascendia">
              <div className="flex flex-col gap-3">
                <a href={`tel:${site.phone}`} className="inline-flex items-center justify-center gap-2 rounded-xl border border-[color:var(--border-subtle)] py-2.5 text-sm text-white hover:bg-white/5"><Phone className="h-4 w-4 text-gold-soft" /> {site.phone}</a>
                <a href={whatsappLink(site)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 rounded-xl border border-[color:var(--border-subtle)] py-2.5 text-sm text-white hover:bg-white/5"><MessageCircle className="h-4 w-4 text-[#25D366]" /> WhatsApp</a>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
}
